import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { ChevronDownIcon } from '@icons';
import * as S from './Dropdown.style';

export type DropdownProps = {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
};

const DropdownContext = createContext<(() => void) | null>(null);

function Dropdown({ label, icon, children, defaultOpen = false, className }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen]);

  return (
    <S.Root ref={rootRef} className={className}>
      <S.Trigger
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
      >
        {icon ? <S.TriggerIcon>{icon}</S.TriggerIcon> : null}
        <S.TriggerLabel>{label}</S.TriggerLabel>
        <S.TriggerChevron $open={isOpen} aria-hidden>
          <ChevronDownIcon />
        </S.TriggerChevron>
      </S.Trigger>

      {isOpen ? (
        <S.Menu id={menuId} role="menu">
          <DropdownContext.Provider value={() => setIsOpen(false)}>
            {children}
          </DropdownContext.Provider>
        </S.Menu>
      ) : null}
    </S.Root>
  );
}

type DropdownItemProps = ButtonHTMLAttributes<HTMLButtonElement>;

function DropdownItem({ type = 'button', onClick, children, ...props }: DropdownItemProps) {
  const closeMenu = useContext(DropdownContext);

  return (
    <S.Item
      type={type}
      role="menuitem"
      onClick={(event) => {
        onClick?.(event);
        closeMenu?.();
      }}
      {...props}
    >
      {children}
    </S.Item>
  );
}

Dropdown.Item = DropdownItem;

export default Dropdown;
